import useSearchHighlight from '@rio-cloud/rio-uikit/hooks/useSearchHighlight';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import TableViewToggles, { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { useGetOrdersQuery, useGetOrdersWithTruckNamesQuery } from '#api-bridge/orderApi.ts';
import { useGetVehiclesQuery } from '#api-bridge/resourcesApi.ts';
import { useApplicationLayoutBottomBar } from '#components/applayout/ApplicationLayoutBottomBarContext.tsx';
import { ServiceInMaintenance } from '#components/error/ServiceInMaintenance.tsx';
import { OrderDialog, type OrderDialogState } from '#components/order/CreateOrderDialog/OrderDialog.tsx';
import { getOrderTableColumns, OrderTable, type OrderTableColumnConfig } from '#components/order/OrderTable.tsx';
import { OrderTableToolbar } from '#components/order/OrderTableToolbar.tsx';
import { OrderDetailsSidebar } from '#components/order/sidebar/OrderDetailsSidebar.tsx';
import { MissingPocketDriverDataSourceBanner } from '#components/resources/MissingPocketDriverDataSourceBanner.tsx';
import { MissingPocketDriverDataSourceBannerStateSync } from '#components/resources/MissingPocketDriverDataSourceBannerState.tsx';
import ErrorFallback from '#components/shared/ErrorFallback.tsx';
import { CreateTourDialog } from '#components/tour/CreateTourDialog/CreateTourDialog.tsx';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import type { OrderType } from '#domain/Order.ts';
import { hasMissingPocketDriverDataSource } from '#domain/VehicleWarning.ts';
import { CsvImportDialog } from '#features/csvImport/CsvImportDialog.tsx';
import { CancelSelectedOpenOrdersConfirmationDialog } from '#features/orders/ui/CancelSelectedOpenOrdersConfirmationDialog.tsx';
import { useFilteredOrders } from '#hooks/order/useFilteredOrders.ts';
import { useDebouncedSearch } from '#hooks/shared/useDebouncedSearch.ts';
import { usePersistedTableColumns } from '#hooks/shared/usePersistedTableColumns.ts';
import { parseAsArrayOf, stringParser, useSearchParam } from '#hooks/shared/useSearchParam.ts';
import { useTableSelection } from '#hooks/shared/useTableSelection.ts';
import { useTableSorting } from '#hooks/shared/useTableSorting.ts';
import { useAppNavigate } from '#routes/AppNavLink.tsx';
import { routes } from '#routes/routes.ts';
import { isHttpUnavailableStatusCodeError } from '#utils/errorCheck.ts';
import { addSortableOrderKey, type OrderWithSortableKey } from '#utils/orders.util.ts';
import { handleNotificationForUpdateData } from '#utils/util.ts';

const ORDER_SORTABLE_COLUMN_KEYS = [
    'number',
    'customerName',
    'loadingAddressEndDate',
    'unloadingAddressEndDate',
    'tourName',
    'truckName',
] as const;

export const OrderPage = () => {
    const intl = useIntl();
    const showNewTourDialog = useFeatureToggle('showNewCreateTourDialog', false);
    const showServiceOrders = useFeatureToggle('showServiceOrders', false);
    const tableRef = useRef<HTMLDivElement | null>(null);

    const [ordersToCreateTour, setOrdersToCreateTour] = useState<OrderWithSortableKey[]>([]);
    const [orderDialogState, setOrderDialogState] = useState<OrderDialogState>('hidden');
    const [orderDialogCreateType, setOrderDialogCreateType] = useState<OrderType>('transport');
    const [showCsvImportDialog, setShowCsvImportDialog] = useState(false);
    const [showCancelSelectedOpenOrdersDialog, setShowCancelSelectedOpenOrdersDialog] = useState(false);
    const [tableViewType, setTableViewType] = useState<TableViewTogglesViewType>(TableViewToggles.VIEW_TYPE_TABLE);
    const defaultTableColumns = useMemo(
        () => getOrderTableColumns(intl, showServiceOrders),
        [intl.locale, showServiceOrders]
    );
    const {
        columns: tableColumns,
        handleColumnOrderChange,
        handleColumnResize,
        handleColumnVisibilityChange,
        resetColumns: resetTableColumns,
        resetVersion: tableResetVersion,
    } = usePersistedTableColumns<OrderTableColumnConfig>({
        defaultColumns: defaultTableColumns,
        storageKeyPrefix: 'order-communication.order.table',
    });

    const navigate = useAppNavigate();
    const { orderId: selectedOrderId } = useParams();
    const { search, searchInput, onSearch } = useDebouncedSearch();
    const [statusFilter, setStatusFilter, resetStatusFilter] = useSearchParam(
        'status',
        parseAsArrayOf(stringParser).default([])
    );
    const [typeFilter, setTypeFilter, resetTypeFilter] = useSearchParam(
        'types',
        parseAsArrayOf(stringParser).default(['transport', 'service'])
    );
    const orderTypes: OrderType[] = showServiceOrders
        ? typeFilter.filter((type): type is OrderType => type === 'transport' || type === 'service')
        : ['transport'];

    const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState<boolean>(false);

    const showCreateTourDialog = ordersToCreateTour.length > 0;
    const skipOrderFetch = showCreateTourDialog || isDocumentDialogOpen || orderDialogState !== 'hidden';
    const {
        isLoading,
        data = [],
        error,
        refetch,
        isFetching,
    } = useGetOrdersWithTruckNamesQuery(
        {
            pollingInterval: 30000,
            skipPollingIfUnfocused: true,
            skip: skipOrderFetch,
        },
        orderTypes
    );
    const dialogOrdersQuery = useGetOrdersQuery(
        { skip: !showCreateTourDialog || !showNewTourDialog },
        showServiceOrders ? ['transport', 'service'] : ['transport']
    );
    const dialogOrders = useMemo(() => {
        const candidates = dialogOrdersQuery.currentData?.filter(order => order.status === 'open') ?? [];
        // Preserve the initial selection while the independent query loads or fails.
        return [...new Map([...ordersToCreateTour, ...candidates].map(order => [order.id, order])).values()];
    }, [dialogOrdersQuery.currentData, ordersToCreateTour]);
    const { data: vehicles = [] } = useGetVehiclesQuery();
    const orderWithSortableKey = useMemo(
        () => addSortableOrderKey(data.filter(order => orderTypes.includes(order.type ?? 'transport'))),
        [data, typeFilter, showServiceOrders]
    );
    const openOrders = useMemo(
        () => orderWithSortableKey.filter(order => order.status === 'open'),
        [orderWithSortableKey]
    );

    const { selectedRowIds, onSelectMultipleRows } = useTableSelection();
    const { sortedItems, sortKey, sortDirection, handleSortByColumn } = useTableSorting(
        orderWithSortableKey,
        'statusWeight',
        'unloadingAddressEndDate',
        ORDER_SORTABLE_COLUMN_KEYS
    );

    const { filteredOrders, selectedOrders, selectableOrders, activeOrder } = useFilteredOrders(
        sortedItems,
        search,
        statusFilter,
        selectedRowIds,
        selectedOrderId
    );

    useSearchHighlight({
        ref: tableRef,
        query: searchInput,
        caseSensitive: false,
        deps: [filteredOrders],
    });

    const selectOrder = (orderId: string | undefined) => {
        navigate(orderId ? `${routes.ORDERS}/${orderId}` : routes.ORDERS);
    };

    const handleOrdersRefetch = useCallback(() => {
        refetch().then(({ isSuccess }) => {
            if (isSuccess) {
                handleNotificationForUpdateData(intl);
            }
        });
    }, [intl.locale, refetch]);

    const onFilterStatus = (input: string[]) => setStatusFilter(input);
    const refreshBottomBar = useMemo(
        () => ({
            ariaLabel: intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.orders.refresh.action',
                defaultMessage: 'Refresh orders',
            }),
            isRefreshing: isFetching,
            onRefresh: handleOrdersRefetch,
            trackingKey: 'intl-msg:ordercom-cloud-frontend.orders.refresh.action',
        }),
        [handleOrdersRefetch, intl.locale, isFetching]
    );

    useApplicationLayoutBottomBar(isLoading || error ? null : refreshBottomBar);

    if (isHttpUnavailableStatusCodeError(error)) {
        return <ServiceInMaintenance />;
    }

    if (error) {
        return <ErrorFallback onRetry={refetch} />;
    }

    if (isLoading) {
        return <Spinner />;
    }

    const hasNoOrders = orderWithSortableKey.length === 0;
    const missingPocketDriverDataSourceCount = vehicles.filter(hasMissingPocketDriverDataSource).length;

    const handleSelectAllOpenOrders = () => {
        onSelectMultipleRows(selectableOrders.map(order => order.id));
    };

    const handleDeselectAllOpenOrders = () => {
        onSelectMultipleRows(null);
    };

    const openCancelSelectedOpenOrdersDialog = () => setShowCancelSelectedOpenOrdersDialog(true);
    const closeCancelSelectedOpenOrdersDialog = () => setShowCancelSelectedOpenOrdersDialog(false);

    const openCreateTourDialogForSelectedOrders = () => setOrdersToCreateTour([...selectedOrders]);
    const openCreateOrderDialog = (type: OrderType = 'transport') => {
        setOrderDialogCreateType(type);
        setOrderDialogState('createOrder');
    };
    const openCsvImportDialog = () => setShowCsvImportDialog(true);
    const openEditOrderDialog = () => setOrderDialogState('editOrder');
    const openDuplicateTourDialog = () => setOrderDialogState('duplicateOrder');

    const openCreateTourDialogForActiveOrder = () => {
        if (activeOrder) {
            setOrdersToCreateTour([activeOrder]);
        }
    };

    const closeCreateTourDialog = () => setOrdersToCreateTour([]);

    return (
        <>
            <MissingPocketDriverDataSourceBannerStateSync missingCount={missingPocketDriverDataSourceCount} />
            <MissingPocketDriverDataSourceBanner vehicles={vehicles} />
            <OrderTableToolbar
                selectedOrders={selectedOrders}
                onOpenCreateTourDialog={openCreateTourDialogForSelectedOrders}
                onOpenCreateOrderDialog={openCreateOrderDialog}
                onOpenCsvImportDialog={openCsvImportDialog}
                searchInput={searchInput}
                onSearch={onSearch}
                statusFilter={statusFilter}
                onFilterStatus={onFilterStatus}
                onResetStatusFilter={resetStatusFilter}
                typeFilter={typeFilter}
                onFilterType={setTypeFilter}
                onResetTypeFilter={resetTypeFilter}
                columns={tableColumns}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                onResetColumns={resetTableColumns}
                onViewTypeChange={setTableViewType}
                viewType={tableViewType}
                showServiceOrders={showServiceOrders}
            />
            <OrderTable
                key={tableResetVersion}
                tableRef={tableRef}
                activeOrderId={selectedOrderId}
                hasNoOrders={hasNoOrders}
                orders={filteredOrders}
                selectedOrderIds={selectedRowIds}
                selectedOrdersCount={selectedOrders.length}
                selectableOrdersCount={selectableOrders.length}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={handleSortByColumn}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnResize={handleColumnResize}
                onRowClick={selectOrder}
                onViewTypeChange={setTableViewType}
                onSelectionChange={onSelectMultipleRows}
                onOpenCreateOrderDialog={openCreateOrderDialog}
                onOpenCreateTourDialog={openCreateTourDialogForSelectedOrders}
                onSelectAllOpenOrders={handleSelectAllOpenOrders}
                onDeselectAllOpenOrders={handleDeselectAllOpenOrders}
                onCancelSelectedOpenOrders={openCancelSelectedOpenOrdersDialog}
                columns={tableColumns}
                viewType={tableViewType}
            />
            <CancelSelectedOpenOrdersConfirmationDialog
                show={showCancelSelectedOpenOrdersDialog}
                selectedOrderIds={selectedOrders.map(order => order.id)}
                onClose={closeCancelSelectedOpenOrdersDialog}
            />
            <CreateTourDialog
                isOpen={showCreateTourDialog}
                close={closeCreateTourDialog}
                orders={ordersToCreateTour}
                availableOrders={showNewTourDialog ? dialogOrders : openOrders}
                isLoadingOrders={showNewTourDialog && dialogOrdersQuery.isFetching && !dialogOrdersQuery.currentData}
                isOrderError={showNewTourDialog && dialogOrdersQuery.isError}
                onRetryOrders={dialogOrdersQuery.refetch}
            />
            <OrderDialog
                state={orderDialogState}
                createType={orderDialogCreateType}
                setActiveOrder={selectOrder}
                onClose={() => setOrderDialogState('hidden')}
            />
            {showCsvImportDialog && (
                <CsvImportDialog
                    onClose={() => setShowCsvImportDialog(false)}
                    onOrdersCreated={() => {
                        refetch().catch(console.error);
                    }}
                />
            )}
            {activeOrder && (
                <OrderDetailsSidebar
                    order={activeOrder}
                    selectedOrdersCount={selectedOrders.length}
                    skipPolling={orderDialogState !== 'hidden' || isDocumentDialogOpen}
                    close={() => selectOrder(undefined)}
                    setDocumentDialogState={setIsDocumentDialogOpen}
                    handlePlanOrder={openCreateTourDialogForActiveOrder}
                    handleDuplicateOrder={openDuplicateTourDialog}
                    handleEditOrder={() => {
                        openEditOrderDialog();
                    }}
                />
            )}
        </>
    );
};
